/**
 * Карточка камня.
 *
 * Главное здесь — не значения, а их источники. Рядом с каждым полем стоит
 * метка, откуда оно взялось, и нажатие раскрывает ту самую фразу из диктовки.
 * Поле, которого в речи не было, остаётся пустым: догадка хуже пустоты.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { db, enqueue } from '../db';
import { apiFetch, syncNow } from '../sync';
import { pictureUrl, releasePictures } from '../lib/pictures';
import { frameFromFile, VoiceRecorder } from '../lib/media';
import { saveVoiceNote, saveNoteText, NOTE_PROBLEM_TEXT, type NoteProblem } from '../lib/notes';
import {
  ulid, resolveCurrent, ATTRIBUTES, ATTRIBUTE_ORDER, STATUSES, STATUS_LABEL,
  parseMoney, pricePerCarat, formatMoney, plural, normalizeCurrency, minorDigits,
  type AttributeKey, type Status, type Fact,
} from '@core';
import type { LocalStone, LocalFact, LocalMedia, LocalVoice, LocalSupplier, LocalCert } from '../db';
import { ContactSheet, ShareSheet } from './StoneActions';
import { MoneySheet } from './MoneySheet';
import { deviceCode } from '../sync';
import { localNo as makeLocalNo } from '@core';

// Коротко — метка стоит в строке рядом со значением и не должна его сжимать.
// Полное объяснение источника раскрывается по нажатию.
const CONF_LABEL: Record<string, string> = {
  certified: 'Сертификат',
  assessed: 'Оценка',
  claimed: 'Заявлено',
  low: 'Проверить',
};

/** Деньги в строке показываем так же, как в блоке цены: с символом валюты. */
function showValue(f: { valueText?: string | null; valueNum?: number | null; unit?: string | null },
                   kind?: string, currency = 'USD'): string {
  if (kind === 'money' && f.valueNum != null) {
    const m = parseMoney(f.valueNum, currency);
    if (m) return formatMoney(m);
  }
  return `${f.valueText ?? ''}${f.unit ? ` ${f.unit}` : ''}`;
}

interface Dupe {
  stoneId: string; no?: string | null; supplier?: string | null;
  reason: string; level: 'same' | 'maybe'; capturedAt?: string;
  price?: number | null; perCt?: number | null;
}

interface PriceEvent {
  attribute: string; value_num: number; unit?: string | null;
  source_type: string; recorded_at: string;
}

export default function StoneView({ stoneId, onBack, onScanCert, onOpenStone, backLabel }: {
  stoneId: string; onBack: () => void;
  onScanCert?: (stone: LocalStone) => void;
  onOpenStone?: (id: string) => void;
  /**
   * Куда ведёт стрелка возврата. По умолчанию — к стенду, но карточку
   * открывают и поверх съёмки: тогда она возвращает в камеру, и подписывать
   * её названием стенда было бы враньём.
   */
  backLabel?: string;
}) {
  const [stone, setStone] = useState<LocalStone | null>(null);
  const [supplier, setSupplier] = useState<LocalSupplier | null>(null);
  const [facts, setFacts] = useState<LocalFact[]>([]);
  const [media, setMedia] = useState<LocalMedia[]>([]);
  const [voices, setVoices] = useState<LocalVoice[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string>('');
  const [reports, setReports] = useState<LocalCert[]>([]);
  const [verifying, setVerifying] = useState('');
  const [dupes, setDupes] = useState<Dupe[]>([]);
  const [priceLog, setPriceLog] = useState<PriceEvent[]>([]);
  const [sheet, setSheet] = useState<'' | 'contact' | 'share' | 'split'>('');
  const [shareUrl, setShareUrl] = useState('');
  const [children, setChildren] = useState<LocalStone[]>([]);
  const addShot = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState('');
  // Запись заметки: к какому кадру говорим ('' — про камень целиком),
  // null — не записываем вовсе.
  const [recFor, setRecFor] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [justShot, setJustShot] = useState('');
  const [noteProblem, setNoteProblem] = useState<NoteProblem>('');
  const [micProblem, setMicProblem] = useState('');
  // Валюта места — на случай, когда поставщик свою не назвал: в Гонконге
  // цену без уточнения говорят в гонконгских долларах.
  const [placeCurrency, setPlaceCurrency] = useState('USD');
  // Нажатая цена: показать ту же сумму в другой валюте.
  const [fx, setFx] = useState<{ value: number; caption: string } | null>(null);
  /** Из минорных единиц в обычные: у иены их ноль, делить на сто нельзя. */
  const major = (m: { minor: number; currency: string }) => m.minor / 10 ** minorDigits(m.currency);
  const recorder = useRef(new VoiceRecorder());

  /**
   * Доснять уже заведённый камень.
   *
   * Раньше кадры принимала только съёмка: вышел из неё — и добавить вид
   * с другой стороны, на просвет или бирку с ценой было нечем. Берём снимок
   * системной камерой и сжимаем его так же, как свой собственный.
   */
  async function addPhoto(file: File) {
    if (!stone) return;
    setAdding('Сохраняю кадр…');
    try {
      const { full } = await frameFromFile(file);
      const now = new Date().toISOString();
      // Роль «photo», а не «preview»: обложкой камня остаётся первый кадр —
      // тот, по которому его узнают в каталоге. Досняли вид на просвет —
      // он встаёт в ленту, а не подменяет собой карточку.
      const mediaId = ulid();
      await db.media.put({
        id: mediaId, subjectId: stone.id, role: 'photo', blob: full,
        mime: full.type, bytes: full.size, uploaded: false, createdAt: now,
      });
      await load();
      setAdding('');
      // Сказать о кадре надо сразу, пока камень в руках: через час человек
      // уже не вспомнит, чем этот снимок отличался от предыдущего.
      setJustShot(mediaId);
      void syncNow();
    } catch (e: any) {
      setAdding(`Не вышло: ${e?.message ?? e}`);
      setTimeout(() => setAdding(''), 3500);
    }
  }

  /** Начать диктовать. photoId пустой — заметка про камень целиком. */
  async function startNote(photoId: string) {
    setNoteProblem('');
    try {
      await recorder.current.start();
      setRecFor(photoId);
    } catch {
      // Причина именно эта — микрофон, а не связь и не вход. Валить всё
      // на «нет связи» уже однажды запутало заказчика.
      setMicProblem('Микрофон недоступен. Разрешите доступ к нему в настройках сайта — в Chrome это замок слева от адреса.');
      setTimeout(() => setMicProblem(''), 6000);
    }
  }

  async function stopNote() {
    const photoId = recFor ?? '';
    setRecFor(null);
    setWorking(true);
    const out = await recorder.current.stop();
    if (!out || !stone) { setWorking(false); return; }

    const res = await saveVoiceNote({
      stoneId: stone.id, blob: out.blob, mime: out.mime,
      durationMs: out.durationMs, photoMediaId: photoId || undefined,
    });
    setNoteProblem(res.problem);
    setJustShot('');
    await load();
    setWorking(false);
  }

  async function load() {
    const s = await db.stones.get(stoneId);
    setStone(s ?? null);
    if (s) setSupplier((await db.suppliers.get(s.supplierId)) ?? null);
    if (s) {
      const place = await db.exhibitions.get(s.exhibitionId);
      setPlaceCurrency(place?.currency || 'USD');
    }
    setFacts(await db.facts.where('subjectId').equals(stoneId).toArray());
    const m = await db.media.where('subjectId').equals(stoneId).toArray();
    setMedia(m);
    setVoices(await db.voices.where('subjectId').equals(stoneId).toArray());
    setReports(await db.certs.where('stoneId').equals(stoneId).toArray());
    setChildren(await db.stones.where('parcelId').equals(stoneId).sortBy('createdAt'));

    const next: Record<string, string> = {};
    for (const it of m) {
      if (it.role === 'audio') continue;
      const u = pictureUrl(it);
      if (u) next[it.id] = u;
    }
    setUrls(next);
  }

  // История цен приходит с сервера: там лежат и вытесненные факты,
  // которых на устройстве уже нет.
  useEffect(() => {
    setPriceLog([]);
    if (!navigator.onLine) return;
    let dead = false;
    void (async () => {
      try {
        const res = await apiFetch(`/api/stones/${stoneId}/prices`);
        if (res.ok && !dead) setPriceLog(((await res.json()) as { prices: PriceEvent[] }).prices);
      } catch { /* история — дополнение, не обязанность */ }
    })();
    return () => { dead = true; };
  }, [stoneId]);

  // Совпадения считает сервер: у него весь каталог, а не то, что успело
  // доехать на этот телефон. Без сети блок просто не показывается.
  useEffect(() => {
    setDupes([]);
    if (!navigator.onLine) return;
    let dead = false;
    void (async () => {
      try {
        const res = await apiFetch(`/api/duplicates?stoneId=${encodeURIComponent(stoneId)}`);
        if (!res.ok || dead) return;
        const d = (await res.json()) as { duplicates: Dupe[] };
        setDupes(d.duplicates ?? []);
      } catch { /* совпадения — подсказка, а не обязанность */ }
    })();
    return () => { dead = true; };
  }, [stoneId, reports.length]);

  /**
   * Расшифровка приходит через пару секунд после записи, а карточка читается
   * один раз при открытии. Открыв её сразу после диктовки, человек видел
   * «ждёт связи» и не понимал, почему текста нет: он к тому моменту уже был.
   * Пока хоть одна заметка не разобрана — перечитываем.
   */
  useEffect(() => {
    if (!voices.some((v) => v.status === 'pending')) return;
    const t = setInterval(() => void load(), 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voices]);

  useEffect(() => {
    void load();
    return () => releasePictures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stoneId]);

  // Победивший факт по каждому атрибуту — тем же правилом, что на сервере.
  const current = useMemo(
    () => resolveCurrent(facts.map((f) => ({
      ...f, subjectType: 'stone', supersededBy: null,
    })) as unknown as Fact[]),
    [facts],
  );

  const photos = media.filter((m) => m.role === 'preview' || m.role === 'photo');
  const certs = media.filter((m) => m.role === 'certificate');
  const cover = photos.find((m) => m.role === 'preview') ?? photos[0];
  const note = voices.find((v) => v.transcript);
  // Заметок у камня может быть много: одна с первой съёмки, остальные —
  // к доснятым кадрам. Показываем все, свежие сверху.
  const notes = useMemo(
    () => voices
      .filter((v) => v.subjectKind !== 'session' && (v.transcript || v.status === 'pending'))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [voices],
  );

  // Цена за карат бывает двух разных сортов, и путать их нельзя:
  // либо её назвал поставщик, либо её посчитал код. Показываем, что именно.
  // В какой валюте названы цены: поставщик в Бангкоке нередко говорит в батах,
  // и показать их долларами значит ошибиться в тридцать пять раз.
  const currency = normalizeCurrency(current.get('currency')?.valueText) ?? placeCurrency;
  const priceFact = current.get('asking_price');
  const perCtFact = current.get('asking_price_per_ct');
  const lotFact = current.get('asking_price_per_ct_lot');
  const weightFact = current.get('weight_ct');

  const money = priceFact?.valueNum != null ? parseMoney(priceFact.valueNum, currency) : null;
  const quotedPerCt = perCtFact?.valueNum != null ? parseMoney(perCtFact.valueNum, currency) : null;
  // Цена лотом — всегда со слов поставщика: вычислить её не из чего.
  const lotPerCt = lotFact?.valueNum != null ? parseMoney(lotFact.valueNum, currency) : null;
  const computedPerCt = money && weightFact?.valueNum
    ? pricePerCarat(money, Number(weightFact.valueNum))
    : null;
  const perCt = quotedPerCt ?? computedPerCt;
  const perCtLabel = quotedPerCt ? 'За карат · со слов' : 'За карат · вычислено';

  /**
   * Убрать камень из каталога.
   *
   * Не стираем: на сервере остаются и запись, и все её факты — вернуть можно
   * запросом. Но из каталога, витрины и поиска двойников камень исчезает
   * у всех, поэтому спрашиваем прямо, а кнопка стоит в самом низу.
   */
  async function removeStone() {
    if (!stone) return;
    const no = stone.publicNo ?? stone.localNo;
    const ok = window.confirm(
      `Убрать ${no} из каталога?\n\n`
      + 'Камень исчезнет у всех, кто работает с этим каталогом, и с витрины '
      + 'покупателя. Снимки и записанное останутся на сервере — вернуть можно.');
    if (!ok) return;
    await db.stones.delete(stone.id);
    await enqueue('stone.remove', { id: stone.id }, ulid());
    void syncNow();
    onBack();
  }

  async function setStatus(s: Status) {
    if (!stone) return;
    await db.stones.update(stone.id, { status: s });
    await enqueue('status.set', { stoneId: stone.id, status: s }, ulid());
    void syncNow();
    void load();
  }

  /**
   * Показ в магазине. Полный слепок камня уезжает вместе с флагом:
   * частичный upsert затёр бы статус на сервере.
   */
  async function setShop(patch: { inCatalog?: boolean; promo?: boolean }) {
    if (!stone) return;
    const next = { ...stone, ...patch };
    if (patch.inCatalog === false) next.promo = false;   // промо без каталога не бывает
    if (patch.promo) next.inCatalog = true;
    await db.stones.update(stone.id, { inCatalog: next.inCatalog, promo: next.promo });
    setStone(next);
    await enqueue('stone.upsert', { ...next }, ulid());
    void syncNow();
  }

  /** Подтвердить значение, предложенное моделью: оно становится наблюдением геммолога. */
  async function confirm(attr: string) {
    const f = current.get(attr);
    if (!f || !stone) return;
    const fact = {
      id: ulid(), subjectType: 'stone', subjectId: stone.id, attribute: attr,
      valueText: f.valueText ?? null, valueNum: f.valueNum ?? null, unit: f.unit ?? null,
      sourceType: 'gemologist', confidence: 'assessed',
      sourceRef: { confirmedFrom: f.id }, needsReview: false,
      recordedAt: new Date().toISOString(),
    };
    await db.facts.put(fact as never);
    await enqueue('fact.add', fact, ulid());
    void syncNow();
    void load();
  }

  /**
   * Отметить отчёт проверенным. После этого его данные перестают быть
   * заявлением и становятся фактом — ради этого шага распознавание
   * и проверка и разведены.
   */
  async function markVerified(cert: LocalCert) {
    setVerifying(cert.id);
    try {
      const res = await apiFetch(`/api/certificates/${cert.id}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: cert.verifyMethod ?? 'manual' }),
      });
      if (res.ok) {
        await db.certs.update(cert.id, { verifiedAt: new Date().toISOString() });
        void syncNow();
        await load();
      }
    } finally {
      setVerifying('');
    }
  }

  /**
   * Разделить партию на камни.
   *
   * Дети наследуют общие свойства партии, но с честным источником: «выведено
   * из предыдущего» — самым младшим по старшинству. Любая диктовка, тег или
   * сертификат на конкретном камне перебьёт унаследованное, не споря с ним.
   */
  async function splitParcel(count: number) {
    if (!stone || !supplier) return;
    const ex = await db.exhibitions.get(stone.exhibitionId);
    const dev = await deviceCode();
    const now = new Date().toISOString();

    const INHERIT = ['stone_type', 'variety', 'natural_synthetic', 'origin', 'treatment',
                     'color', 'clarity', 'shape', 'cut',
                     'asking_price_per_ct', 'weight_min_ct', 'weight_max_ct'];

    let seq = supplier.seq;
    for (let i = 0; i < count; i++) {
      seq += 1;
      const id = ulid();
      const child: LocalStone = {
        id, parcelId: stone.id, sessionId: stone.sessionId,
        localNo: makeLocalNo(ex?.code ?? 'SHOW', supplier.code, dev, seq),
        kind: 'stone', supplierId: stone.supplierId, exhibitionId: stone.exhibitionId,
        status: 'viewed', capturedAt: now, createdAt: now,
      };
      await db.stones.put(child);
      await enqueue('stone.upsert', { ...child, parcelId: stone.id }, ulid());

      for (const attr of INHERIT) {
        const f = current.get(attr);
        if (!f) continue;
        const fact = {
          id: ulid(), subjectType: 'stone', subjectId: id, attribute: attr,
          valueText: f.valueText ?? null, valueNum: f.valueNum ?? null, unit: f.unit ?? null,
          sourceType: 'inferred_from_previous', confidence: 'low',
          sourceRef: { parcelId: stone.id }, needsReview: false, recordedAt: now,
        };
        await db.facts.put(fact as never);
        await enqueue('fact.add', fact, ulid());
      }
    }
    await db.suppliers.update(supplier.id, { seq });
    setSheet('');
    void syncNow();
    void load();
  }

  if (!stone) return <div className="blankslate">Камень не найден.</div>;

  const wMin = current.get('weight_min_ct')?.valueNum;
  const wMax = current.get('weight_max_ct')?.valueNum;
  const qty = current.get('quantity')?.valueNum;
  const title = [
    qty ? `${qty} ×` : null,
    current.get('stone_type')?.valueText,
    weightFact?.valueNum ? `${weightFact.valueNum} ct`
      : wMin && wMax ? `${wMin}—${wMax} ct` : null,
  ].filter(Boolean).join(' ') || 'Без описания';

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>
          ‹ {backLabel ?? supplier?.companyName ?? 'Назад'}
        </button>
      </div>

      <div className="scroll">
        {/* Снимок и заголовок в одной строке, как фото и шапка в отчёте:
            глазу не нужно прыгать между «что это» и «как выглядит». */}
        <div className="stonehead">
          <div className="plate sq">
            {cover ? <img src={urls[cover.id]} alt="камень" /> : <span className="blank">{stone.kind === 'parcel' ? '⁘' : '◆'}</span>}
          </div>
          <div className="grow">
            <p className="meta code">{stone.publicNo ?? stone.localNo}{stone.publicNo ? '' : ' · ждёт синка'}</p>
            <h1 className="t">{title}</h1>
            <p className="meta">
              {new Date(stone.capturedAt).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
        {photos.length + certs.length > 1 && (
          <div className="filmstrip">
            {[...photos, ...certs].slice(0, 8).map((m) => (
              <img key={m.id} src={urls[m.id]} alt={m.role === 'certificate' ? 'сертификат' : 'кадр'} />
            ))}
          </div>
        )}

        {/* Камень часто доснимают позже: вид на просвет, другая сторона,
            бирка с ценой. Файл берём системной камерой — свою здесь
            разворачивать не из-за чего. */}
        <input ref={addShot} type="file" accept="image/*" capture="environment" hidden
               onChange={(e) => {
                 const f = e.target.files?.[0];
                 e.target.value = '';
                 if (f) void addPhoto(f);
               }} />
        {(money || perCt || lotPerCt) && (
          <div className={`money${[money, perCt, lotPerCt].filter(Boolean).length > 2 ? ' three' : ''}`}>
            {/* Любая цена нажимается: «а сколько это в долларах» — вопрос,
                который у стенда задают по десять раз в день. */}
            {money && (
              <div className="tappable" onClick={() => setFx({ value: major(money), caption: 'за всё' })}>
                <span>Просят</span><b>{formatMoney(money)}</b>
              </div>
            )}
            {perCt && (
              <div className={`tappable${quotedPerCt ? '' : ' calc'}`}
                   onClick={() => setFx({ value: major(perCt), caption: 'за карат' })}>
                <span>{quotedPerCt && lotPerCt ? 'За карат · с выбором' : perCtLabel}</span>
                <b>{formatMoney(perCt)}</b>
              </div>
            )}
            {/* Цена лотом стоит рядом с ценой с выбором: торг идёт ровно
                за расстояние между ними. */}
            {lotPerCt && (
              <div className="tappable"
                   onClick={() => setFx({ value: major(lotPerCt), caption: 'за карат лотом' })}>
                <span>За карат · лотом</span><b>{formatMoney(lotPerCt)}</b>
              </div>
            )}
          </div>
        )}

        <PriceHistory log={priceLog} />

        <div className="head"><h3>Характеристики</h3><span>источник у каждой</span></div>
        {current.size === 0 ? (
          <div className="blankslate">
            Полей пока нет. Надиктуйте заметку — или дождитесь связи, если запись стоит в очереди.
          </div>
        ) : (
          <div className="attrs">
            {ATTRIBUTE_ORDER.filter((a) => current.has(a)).map((attr) => {
              const f = current.get(attr)!;
              const meta = ATTRIBUTES[attr as AttributeKey];
              const span = (f.sourceRef as any)?.span as [number, number] | null | undefined;
              const isOpen = open === attr;
              return (
                <div key={attr}>
                  <button className="attr" onClick={() => setOpen(isOpen ? '' : attr)}>
                    <span className="k">{meta?.label ?? attr}</span>
                    <span className={`v${meta?.kind === 'num' || meta?.kind === 'money' ? ' num' : ''}`}>
                      {showValue(f, meta?.kind, currency)}
                    </span>
                    <span className={`src src-${f.confidence}`}>{CONF_LABEL[f.confidence] ?? f.confidence}</span>
                  </button>
                  {isOpen && (
                    <div className="reveal">
                      {span && note?.transcript ? (
                        <q>…{note.transcript.slice(Math.max(0, span[0] - 12), Math.min(note.transcript.length, span[1] + 12))}…</q>
                      ) : (
                        <span>Источник: {f.sourceType}</span>
                      )}
                      {f.needsReview && (
                        <div style={{ marginTop: 8 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => void confirm(attr)}>
                            Подтвердить — это моя оценка
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Кадр только что снят — самое время сказать, что на нём. */}
        {justShot && recFor === null && !working && (
          <div className="memo" style={{ marginTop: 10 }}>
            <b>Кадр добавлен.</b> Скажите, что на нём — заметка привяжется
            к этому снимку, а не к камню вообще.
            <div className="states" style={{ marginTop: 10 }}>
              <button className="state-btn" onClick={() => void startNote(justShot)}>
                🎤 Записать к этому кадру
              </button>
              <button className="state-btn" onClick={() => setJustShot('')}>Не нужно</button>
            </div>
          </div>
        )}

        {recFor !== null && (
          <div className="memo warn" style={{ marginTop: 10 }}>
            <b>Идёт запись{recFor ? ' к последнему кадру' : ''}.</b> Говорите как обычно —
            цену, вес, происхождение, что смущает. Текст появится ниже.
          </div>
        )}

        {micProblem && <div className="memo warn" style={{ marginTop: 10 }}>{micProblem}</div>}
        {noteProblem && (
          <div className="memo warn" style={{ marginTop: 10 }}>{NOTE_PROBLEM_TEXT[noteProblem]}</div>
        )}

        {notes.length > 0 && (
          <>
            <div className="head">
              <h3>Заметки</h3>
              <span>{notes.length} · оригинал аудио сохранён</span>
            </div>
            {notes.map((v) => (
              <NoteCard key={v.id} note={v} stoneId={stoneId}
                        photoUrl={v.photoMediaId ? urls[v.photoMediaId] : undefined}
                        onRedo={() => void startNote(v.photoMediaId ?? '')}
                        onSaved={() => void load()} />
            ))}
          </>
        )}

        <div className="states">
          {STATUSES.map((s) => (
            <button key={s} className="state-btn" aria-pressed={stone.status === s} onClick={() => void setStatus(s)}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="head"><h3>Магазин</h3><span>{stone.inCatalog ? 'виден покупателям' : 'скрыт'}</span></div>
        <div className="states" style={{ marginTop: 0 }}>
          <button className="state-btn" aria-pressed={Boolean(stone.inCatalog)}
                  onClick={() => void setShop({ inCatalog: !stone.inCatalog })}>
            В каталог
          </button>
          <button className="state-btn" aria-pressed={Boolean(stone.promo)}
                  disabled={!stone.inCatalog && !stone.promo}
                  onClick={() => void setShop({ promo: !stone.promo })}>
            В промо
          </button>
        </div>

        {/* Связь — конец цепочки: нашёл камень → написал поставщику. */}
        <div className="acts">
          <button className="btn btn-primary" onClick={() => setSheet('contact')}>
            Написать поставщику
          </button>
          <button className="btn btn-secondary" onClick={() => setSheet('share')}>
            Ссылка · QR
          </button>
          {stone.kind === 'parcel' && children.length === 0 && (
            <button className="btn btn-secondary" onClick={() => setSheet('split')}>
              Разделить на камни
            </button>
          )}
        </div>

        <div className="head">
          <h3>Сертификат</h3>
          <span>{reports.length ? `${reports.length}` : 'нет'}</span>
        </div>

        {reports.length === 0 ? (
          <div className="memo" style={{ marginTop: 0 }}>
            Отчёта нет. Снимите его — данные лягут в карточку отдельным
            источником, не смешиваясь со словами поставщика.
            {onScanCert && (
              <button className="btn btn-secondary" style={{ marginTop: 12 }}
                      onClick={() => onScanCert(stone)}>
                Снять сертификат
              </button>
            )}
          </div>
        ) : reports.map((c) => c.scanStatus === 'pending' ? (
          <div className="memo wait" key={c.id} style={{ marginTop: 0, marginBottom: 10 }}>
            <b>Отчёт снят, но ещё не прочитан.</b> Снимок ждёт связи — как только
            он уедет, сервер разберёт его сам и поля появятся здесь. Переснимать не нужно.
          </div>
        ) : c.scanStatus === 'failed' ? (
          <div className="memo warn" key={c.id} style={{ marginTop: 0, marginBottom: 10 }}>
            <b>Отчёт прочитать не удалось.</b> Снимок сохранён, но данные с него не
            снялись{c.scanError ? ` (${c.scanError})` : ''}. Снимите отчёт ещё раз:
            целиком в кадре, без бликов, номер читаем.
            {onScanCert && (
              <button className="btn btn-secondary" style={{ marginTop: 12 }}
                      onClick={() => onScanCert(stone)}>
                Переснять отчёт
              </button>
            )}
          </div>
        ) : (
          <div className="memo" key={c.id} style={{ marginTop: 0, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <b style={{ fontSize: 17 }}>{c.labCode ?? c.labName ?? 'Лаборатория не определена'}</b>
              {c.verifiedAt
                ? <span className="src src-certified">проверен</span>
                : <span className="src src-claimed">не проверен</span>}
            </div>
            {c.reportNo && (
              <div style={{ marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>№ {c.reportNo}</div>
            )}
            {c.reportDate && <div style={{ fontSize: 13.5 }}>от {c.reportDate}</div>}

            {!c.verifiedAt && (
              <>
                <p style={{ fontSize: 14, marginTop: 10, marginBottom: 0 }}>
                  Пока это прочитанная бумага. Данные отчёта помечены как заявленные
                  и в отбор «сертифицировано» не попадают.
                </p>
                <div className="states">
                  {c.verifyUrl && (
                    <a className="state-btn" href={c.verifyUrl} target="_blank" rel="noreferrer"
                       style={{ textDecoration: 'none' }}>
                      Открыть проверку
                    </a>
                  )}
                  <button className="state-btn" aria-pressed disabled={verifying === c.id}
                          onClick={() => void markVerified(c)}>
                    {verifying === c.id ? 'Отмечаю…' : 'Проверил — сходится'}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {reports.length > 0 && onScanCert && (
          <button className="btn btn-secondary" onClick={() => onScanCert(stone)}>
            Снять ещё отчёт
          </button>
        )}

        {dupes.length > 0 && (
          <>
            <div className="head">
              <h3>Похоже на уже снятое</h3>
              <span>{dupes.some((d) => d.level === 'same') ? 'совпал признак' : 'посмотрите'}</span>
            </div>
            <div className="list">
              {dupes.map((d) => (
                <button key={d.stoneId} className="dupe"
                        onClick={() => onOpenStone?.(d.stoneId)} disabled={!onOpenStone}>
                  <span className="grow">
                    <span className="nm">{d.no ?? '—'}</span>
                    <span className="sub">{[d.supplier, d.reason, priceDiff(d, current)]
                      .filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className={`mark ${d.level}`}>
                    {d.level === 'same' ? 'тот же' : 'похож'}
                  </span>
                </button>
              ))}
            </div>
            <div className="memo" style={{ marginTop: 10 }}>
              Ничего не объединено и не удалено — решать вам. Совпавший номер отчёта
              означает тот же камень; совпавший вес у одного поставщика — только повод
              сверить глазами.
            </div>
          </>
        )}

        {children.length > 0 && (
          <>
            <div className="head">
              <h3>Камни этой партии</h3><span>{children.length}</span>
            </div>
            <div className="list">
              {children.map((c) => (
                <button key={c.id} className="row" onClick={() => onOpenStone?.(c.id)}>
                  <span className="grow">
                    <span className="nm">{c.publicNo ?? c.localNo}</span>
                    <span className="sub">{STATUS_LABEL[c.status as Status] ?? c.status}</span>
                  </span>
                  <span className="chev">›</span>
                </button>
              ))}
            </div>
            <div className="memo" style={{ marginTop: 10 }}>
              Свойства партии перешли каждому камню как «выведенные»: диктовка,
              тег или сертификат на конкретном камне их перебьют.
            </div>
          </>
        )}

        {stone.parcelId && (
          <button className="memo" style={{ marginTop: 10, width: '100%', textAlign: 'left' }}
                  onClick={() => onOpenStone?.(stone.parcelId!)}>
            Этот камень — из разделённой партии. <b>Открыть партию ›</b>
          </button>
        )}

        {/* Внизу и тише прочего: убирают редко, а промахнуться легко. */}
        <button className="btn btn-secondary" style={{ marginTop: 24, color: 'var(--alert)' }}
                onClick={() => void removeStone()}>
          Убрать камень из каталога
        </button>
      </div>
      {/* Главные действия у камня — внизу, под большим пальцем: доснять
          и надиктовать. Всё остальное на карточке читается, а не нажимается. */}
      <div className="dock">
        <button className="btn btn-secondary" disabled={Boolean(adding) || recFor !== null || working}
                onClick={() => addShot.current?.click()}>
          {adding || `Ещё кадр${photos.length ? ` · ${photos.length}` : ''}`}
        </button>
        {recFor === null ? (
          <button className="btn btn-primary" disabled={working} onClick={() => void startNote('')}>
            {working ? 'Расшифровываю…' : 'Надиктовать'}
          </button>
        ) : (
          <button className="btn btn-primary" style={{ background: 'var(--alert)' }} onClick={() => void stopNote()}>
            ● Остановить
          </button>
        )}
      </div>


      {fx && (
        <MoneySheet value={fx.value} currency={currency} caption={fx.caption}
                    onClose={() => setFx(null)} />
      )}

      {sheet === 'contact' && supplier && (
        <ContactSheet stone={stone} supplier={supplier}
                      shareUrl={shareUrl || undefined} onClose={() => setSheet('')} />
      )}
      {sheet === 'share' && (
        <ShareSheet stone={stone} onClose={() => setSheet('')}
                    onCreated={(u) => setShareUrl(u)} />
      )}
      {sheet === 'split' && (
        <SplitSheet
          suggested={Number(current.get('quantity')?.valueNum ?? 0) || 4}
          onGo={(n) => void splitParcel(n)}
          onClose={() => setSheet('')}
        />
      )}
    </div>
  );
}

/**
 * Одна заметка: что сказано и к какому кадру.
 *
 * Компонент верхнеуровневый намеренно. Объявленный внутри карточки, он
 * пересоздавался бы на каждый ввод символа, и поле теряло бы фокус после
 * первой же буквы — правкой такое пользоваться нельзя.
 *
 * Текст правится руками: расшифровка ошибается на именах месторождений и
 * на числах, а переписывать всю заметку голосом из-за одной цифры глупо.
 * Аудио при этом остаётся нетронутым — сверить сказанное можно всегда.
 */
function NoteCard({ note, stoneId, photoUrl, onRedo, onSaved }: {
  note: LocalVoice;
  stoneId: string;
  photoUrl?: string;
  onRedo: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(note.transcript ?? '');
  const [saving, setSaving] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);

  /*
   * Поле растёт под текст.
   *
   * Заметку читают целиком, а не прокручивают: прокрутка внутри прокрутки на
   * телефоне — верный способ не заметить вторую половину сказанного. Считать
   * высоту по числу символов нельзя, она зависит от ширины экрана и переносов,
   * поэтому спрашиваем у самого поля, сколько ему нужно.
   */
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  // Пришла расшифровка с сервера — показываем её, пока человек не начал править.
  useEffect(() => {
    setDraft((d) => (d === '' ? note.transcript ?? '' : d));
  }, [note.transcript]);

  const changed = draft.trim() !== (note.transcript ?? '').trim();
  const when = new Date(note.createdAt).toLocaleString('ru-RU',
    { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="notecard">
      <div className="top">
        {photoUrl
          ? <img src={photoUrl} alt="кадр заметки" />
          : <span className="ph">◆</span>}
        <span className="grow">
          <b>{photoUrl ? 'К этому кадру' : 'О камне целиком'}</b>
          <i>{when}</i>
        </span>
      </div>

      {note.status === 'pending' && !note.transcript ? (
        <p className="waiting">
          {navigator.onLine
            ? 'Записано. Расшифровка идёт — текст появится здесь через несколько секунд.'
            : 'Записано. Расшифровка ждёт связи; аудио на устройстве, ничего не потеряно.'}
        </p>
      ) : (
        <textarea ref={box} value={draft} rows={2}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Здесь будет расшифровка — её можно поправить" />
      )}

      <div className="states" style={{ marginTop: 8 }}>
        <button className="state-btn" onClick={onRedo}>Перезаписать</button>
        {changed && (
          <button className="state-btn" aria-pressed disabled={saving}
                  onClick={() => {
                    setSaving(true);
                    void saveNoteText(note.id, stoneId, draft)
                      .then(onSaved)
                      .finally(() => setSaving(false));
                  }}>
            {saving ? 'Сохраняю…' : 'Сохранить текст'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * История запрошенных цен: каждая жила отдельным фактом, здесь они в ряд.
 * Показывается только когда цена менялась — одна цена историей не является.
 */
function PriceHistory({ log }: { log: PriceEvent[] }) {
  const per = log.filter((p) => p.attribute === 'asking_price_per_ct');
  const lot = log.filter((p) => p.attribute === 'asking_price_per_ct_lot');
  const total = log.filter((p) => p.attribute === 'asking_price');
  const track = per.length >= 2 ? per : lot.length >= 2 ? lot : total.length >= 2 ? total : null;
  if (!track) return null;

  const first = track[0];
  const last = track[track.length - 1];
  // value_num хранит основные единицы валюты: 4800 значит $4,800.
  const a = Number(first.value_num);
  const b = Number(last.value_num);
  if (!a || a === b) return null;
  const diff = Math.round(((b - a) / a) * 100);
  const when = (d: string) => new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  const unit = track === per ? ' за карат' : track === lot ? ' за карат лотом' : '';

  return (
    <div className={`memo ${diff < 0 ? '' : 'warn'}`}>
      <b>Цена менялась.</b> {when(first.recorded_at)} просили ${Math.round(a).toLocaleString('ru-RU')}{unit},
      сейчас ${Math.round(b).toLocaleString('ru-RU')} — {diff > 0 ? `+${diff}` : diff}%.
    </div>
  );
}

/** Разница за карат с найденным двойником — словами, не таблицей. */
function priceDiff(d: Dupe, current: Map<string, Fact>): string | null {
  const mine = current.get('asking_price_per_ct')?.valueNum
    ?? (() => {
      const p = current.get('asking_price')?.valueNum;
      const w = current.get('weight_ct')?.valueNum;
      return p && w ? Number(p) / Number(w) : null;
    })();
  if (mine == null || d.perCt == null) return null;
  const diff = Math.round(((Number(mine) - Number(d.perCt)) / Number(d.perCt)) * 100);
  if (!diff) return 'цена та же';
  return diff < 0 ? `сейчас дешевле на ${-diff}%` : `сейчас дороже на ${diff}%`;
}

/** Сколько камней в партии: счётчик кнопками, никакой клавиатуры. */
function SplitSheet({ suggested, onGo, onClose }: {
  suggested: number; onGo: (n: number) => void; onClose: () => void;
}) {
  const [n, setN] = useState(Math.max(2, Math.min(60, suggested)));
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <div className="head" style={{ marginTop: 0 }}>
          <h3>Разделить партию</h3><span />
        </div>
        <div className="stepper">
          <button onClick={() => setN((v) => Math.max(2, v - 1))} aria-label="Меньше">−</button>
          <b>{n}</b>
          <button onClick={() => setN((v) => Math.min(60, v + 1))} aria-label="Больше">+</button>
        </div>
        <div className="memo">
          Появится {n} {plural(n, 'карточка', 'карточки', 'карточек')} с номерами по
          порядку. Вид, происхождение, обработка и цена за карат перейдут каждой —
          как выведенные из партии, с правом быть исправленными.
        </div>
        <div className="dock" style={{ position: 'static', padding: '14px 0 0' }}>
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={() => onGo(n)}>Разделить</button>
        </div>
      </div>
    </>
  );
}
